import { GroupingState, SortingState } from "@tanstack/react-table";
import { isEqual } from "lodash";
import { FC, PropsWithChildren, useMemo } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import { PIC } from "@ryde/assets";
import { usePageContext } from "@ryde/redux";
import {
  activeViewIdAtomFamily,
  activeViewSF,
  SortingEnum,
  tableColumnSortingValueSF,
  tableColumnsSortingAF,
  tableGroupingAF,
} from "@ryde/shared";
import { greyMui } from "@ryde/theme";

import {
  GroupByContainerStyled,
  HeaderPopoverWrapperStyled,
} from "./header.popover.styled";

export interface IHeaderPopover extends PropsWithChildren {
  fieldNameResolved: string;
  tableSettingsDrawerToggle?: () => void;
  groupByClick?: () => void;
  sortingDisabled?: boolean;
  groupingDisabled?: boolean;
  colMovingDisabled?: boolean;
  isHiddenFromView?: boolean;
}

export const HeaderPopover: FC<IHeaderPopover> = ({
  fieldNameResolved,
  sortingDisabled,
  groupingDisabled,
  children,
}) => {
  const { t } = useTranslation();
  const { entity } = usePageContext();
  const setColumnSorting = useSetRecoilState(tableColumnsSortingAF(entity));
  const [columnGrouping, setColumnGrouping] = useRecoilState(
    tableGroupingAF(entity),
  );
  const activeView = useRecoilValue(activeViewSF(entity));
  const setActiveViewId = useSetRecoilState(activeViewIdAtomFamily(entity));

  const sorting = useRecoilValue(
    tableColumnSortingValueSF([entity, fieldNameResolved]),
  );
  const sortByClick = useEvent((): void => {
    const newSortingState: SortingState = [
      { id: fieldNameResolved, desc: sorting === SortingEnum.Asc },
    ];

    setColumnSorting?.(newSortingState);

    if (
      activeView &&
      !isEqual(newSortingState, activeView.viewState.columnsSorting)
    ) {
      setActiveViewId(undefined);
    }
  });

  const groupByClick = useEvent((): void => {
    let newGroupingState: GroupingState;

    if (!columnGrouping?.length || columnGrouping[0] !== fieldNameResolved) {
      newGroupingState = [fieldNameResolved];
    } else {
      newGroupingState = [];
    }

    setColumnGrouping(newGroupingState);

    if (
      activeView &&
      !isEqual(newGroupingState, activeView.viewState.columnsGrouping)
    ) {
      setActiveViewId(undefined);
    }
  });

  const popoverButtonsDisabled = useMemo(() => {
    return Boolean(groupingDisabled && sortingDisabled);
  }, [groupingDisabled, sortingDisabled]);

  return (
    <HeaderPopoverWrapperStyled>
      {!!children && (
        <div className="header_text truncate-text">{children}</div>
      )}

      {!popoverButtonsDisabled && (
        <div className="controls_wrapper">
          {!sortingDisabled && sorting === SortingEnum.NotSorted && (
            <PIC.TableSortInitialSVG
              data-e2e="svg-no-sort-popover"
              onClick={sortByClick}
            />
          )}
          {sorting === SortingEnum.Asc && (
            <PIC.TableSortAscSVG
              data-e2e="svg-sort-asc-popover"
              onClick={sortByClick}
            />
          )}
          {sorting === SortingEnum.Desc && (
            <PIC.TableSortDescSVG
              data-e2e="svg-desc-popover"
              onClick={sortByClick}
            />
          )}
          {!groupingDisabled && (
            <GroupByContainerStyled onClick={groupByClick}>
              <label className="groupByTitle">{t("general.GROUP_BY")}</label>
              <PIC.TableGroupSVG
                color={greyMui._700}
                data-e2e="svg-group-by-popover"
              />
            </GroupByContainerStyled>
          )}
        </div>
      )}
    </HeaderPopoverWrapperStyled>
  );
};
